import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Calendar as CalendarIcon, Plus, Trash2, Upload, AlertCircle, X, RefreshCw, Download, ChevronDown, ChevronRight, Check, Loader2 } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { COUNTRIES } from '@/utils/countries';
import { useCountryRegions } from '@/hooks/useCountryRegions';

interface Holiday {
  date: string;
  name?: string;
  source?: 'api' | 'custom';
}

// Import from canonical source
import type { HolidaySyncStatus } from '@/types/companySettings';

interface HolidayCalendarProps {
  holidays: string[]; // ISO date strings (YYYY-MM-DD) from database
  onSave: (holidays: string[], additionalUpdates?: { 
    country_code?: string | null; 
    region_code?: string | null;
    holiday_sync_status?: HolidaySyncStatus;
  }) => Promise<void>;
  isLoading?: boolean;
  countryCode?: string | null;
  regionCode?: string | null;
  onCountryChange?: (countryCode: string) => Promise<void>;
  onRegionChange?: (regionCode: string) => Promise<void>;
  holidaySyncStatus?: HolidaySyncStatus | null;
  holidayAutoSync?: boolean;
  onAutoSyncChange?: (enabled: boolean) => Promise<void>;
}

export function HolidayCalendar({ 
  holidays: initialHolidays, 
  onSave, 
  isLoading,
  countryCode: initialCountryCode,
  regionCode: initialRegionCode,
  onCountryChange,
  onRegionChange,
  holidaySyncStatus,
  holidayAutoSync,
  onAutoSyncChange,
}: HolidayCalendarProps) {
  const { currentCompany } = useAuth();
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [excludedApiDates, setExcludedApiDates] = useState<string[]>(
    () => holidaySyncStatus?.excluded_api_dates ?? []
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [regionCode, setRegionCode] = useState(initialRegionCode);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{
    last_sync?: string;
    count?: number;
    source?: string;
    status?: 'success' | 'error' | 'cache';
    error_message?: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedIndicatorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);
  // Utility function to deduplicate holidays by date
  const deduplicateHolidays = (holidays: Holiday[]): Holiday[] => {
    const seen = new Set<string>();
    const duplicates: Holiday[] = [];
    
    const deduplicated = holidays.filter(holiday => {
      if (seen.has(holiday.date)) {
        duplicates.push(holiday);
        return false;
      }
      seen.add(holiday.date);
      return true;
    });

    // Log if duplicates were found
    if (duplicates.length > 0) {
      console.warn(`Removed ${duplicates.length} duplicate holiday(s):`, duplicates.map(h => h.date));
    }

    return deduplicated;
  };

  // Convert initial string[] to Holiday[] on mount/change and enrich with metadata
  useEffect(() => {
    // Don't overwrite local changes if user is actively editing
    if (isDirty) return;
    
    // Create a map of metadata from sync status
    const metadataMap = new Map<string, { name?: string; source?: 'api' | 'custom' }>();
    if (holidaySyncStatus?.metadata && Array.isArray(holidaySyncStatus.metadata)) {
      holidaySyncStatus.metadata.forEach((item: any) => {
        metadataMap.set(item.date, { name: item.name, source: item.source });
      });
    }
    
    // Enrich holidays with metadata (names and sources)
    const holidayObjects: Holiday[] = (initialHolidays || []).map(date => {
      const meta = metadataMap.get(date);
      return {
        date,
        name: meta?.name,
        source: meta?.source
      };
    });
    
    const deduplicated = deduplicateHolidays(holidayObjects);
    setHolidays(deduplicated);
    
    // Hydrate exclusion list from persisted sync status
    setExcludedApiDates(holidaySyncStatus?.excluded_api_dates ?? []);
  }, [initialHolidays, isDirty, holidaySyncStatus]);

  // Sync countryCode state with prop changes
  useEffect(() => {
    if (initialCountryCode !== undefined) {
      setCountryCode(initialCountryCode);
    }
  }, [initialCountryCode]);

  // Sync regionCode state with prop changes
  useEffect(() => {
    if (initialRegionCode !== undefined) {
      setRegionCode(initialRegionCode);
    }
  }, [initialRegionCode]);

  // Hydrate syncStatus from persisted holidaySyncStatus prop
  useEffect(() => {
    if (holidaySyncStatus) {
      setSyncStatus({
        last_sync: holidaySyncStatus.last_sync,
        count: holidaySyncStatus.holiday_count,
        source: holidaySyncStatus.api_source || 'api',
        status: holidaySyncStatus.status,
        error_message: holidaySyncStatus.error_message
      });
    }
  }, [holidaySyncStatus]);

  // Countries from shared utils — single source of truth
  const countries = COUNTRIES;

  // Fetch available regions for the selected country
  const { regions, isLoading: isLoadingRegions, error: regionsError } = useCountryRegions(countryCode);

  const handleSync = async () => {
    if (!currentCompany?.id || !countryCode) {
      toast({
        title: "Missing Information",
        description: "Please select a country before syncing.",
        variant: "destructive",
      });
      return;
    }

    setIsSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-holidays', {
        body: {
          company_id: currentCompany.id,
          country_code: countryCode,
          region_code: regionCode || undefined,
          year: new Date().getFullYear(),
          merge_with_custom: true,
          excluded_api_dates: excludedApiDates, // Send live exclusions so sync doesn't need a prior Save
        }
      });

      if (error) throw error;

      if (data && data.metadata) {
        // Enrich display with metadata from API and deduplicate
        const deduplicated = deduplicateHolidays(data.metadata);
        setHolidays(deduplicated);
        setSyncStatus({
          last_sync: data.synced_at,
          count: data.count,
          source: data.source
        });
        // Hydrate exclusion list from sync response
        if (data.excluded_api_dates) {
          setExcludedApiDates(data.excluded_api_dates);
        }
        setIsDirty(true);
        
        toast({
          title: "Holidays Synced",
          description: `Synced ${data.count} holidays from Calendarific${excludedApiDates.length > 0 ? ` (${excludedApiDates.length} excluded)` : ''}`,
        });
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync holidays from Calendarific",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddDate = (date: Date | undefined) => {
    if (!date) return;
    
    const dateString = format(date, 'yyyy-MM-dd');
    
    if (holidays.some(h => h.date === dateString)) {
      toast({
        title: "Already Added",
        description: "This date is already in the holiday calendar.",
        variant: "destructive",
      });
      return;
    }

    const newHolidays = [...holidays, { date: dateString, source: 'custom' as const }]
      .sort((a, b) => a.date.localeCompare(b.date));
    const deduplicated = deduplicateHolidays(newHolidays);
    setHolidays(deduplicated);
    setIsDirty(true);
    setSelectedDate(undefined);
    
    toast({
      title: "Holiday Added",
      description: `${format(date, 'PPP')} added to holiday calendar.`,
    });
  };

  const handleRemoveDate = (dateString: string) => {
    const holiday = holidays.find(h => h.date === dateString);
    
    // If it's an API-sourced holiday, show confirmation dialog
    if (holiday && holiday.source === 'api' && holiday.name) {
      setHolidayToDelete(holiday);
      setDeleteDialogOpen(true);
      return;
    }

    // Direct removal for custom holidays
    const newHolidays = holidays.filter(h => h.date !== dateString);
    setHolidays(newHolidays);
    setIsDirty(true);
    
    toast({
      title: "Holiday Removed",
      description: "Custom holiday has been removed.",
    });
  };

  const confirmDeleteApiHoliday = () => {
    if (!holidayToDelete) return;
    
    const newHolidays = holidays.filter(h => h.date !== holidayToDelete.date);
    setHolidays(newHolidays);
    // Persist the exclusion so future syncs don't re-add this date
    setExcludedApiDates(prev => [...new Set([...prev, holidayToDelete.date])]);
    setIsDirty(true);
    setDeleteDialogOpen(false);
    
    toast({
      title: "Holiday Excluded",
      description: `"${holidayToDelete.name}" has been removed and will not be restored by future syncs.`,
    });
    
    setHolidayToDelete(null);
  };

  const handleRestoreExcludedHoliday = (date: string) => {
    // Remove from exclusion list
    setExcludedApiDates(prev => prev.filter(d => d !== date));
    
    // Find metadata for this date from sync status
    const meta = holidaySyncStatus?.metadata?.find(m => m.date === date);
    
    // Re-add to holidays list
    const restored: Holiday = {
      date,
      name: meta?.name,
      source: 'api',
    };
    
    const newHolidays = [...holidays, restored].sort((a, b) => a.date.localeCompare(b.date));
    setHolidays(deduplicateHolidays(newHolidays));
    setIsDirty(true);
    
    toast({
      title: "Holiday Restored",
      description: `"${meta?.name || date}" will be included in future syncs.`,
    });
  };

  const handleBulkImport = () => {
    const lines = bulkInput.split('\n').filter(line => line.trim());
    const validDates: Holiday[] = [];
    const invalidDates: string[] = [];

    for (const line of lines) {
      const trimmed = line.trim();
      
      const date = parseISO(trimmed);
      
      if (isValid(date)) {
        const dateString = format(date, 'yyyy-MM-dd');
        if (!holidays.some(h => h.date === dateString) && 
            !validDates.some(h => h.date === dateString)) {
          validDates.push({ date: dateString, source: 'custom' });
        }
      } else {
        invalidDates.push(trimmed);
      }
    }

    if (validDates.length > 0) {
      const newHolidays = [...holidays, ...validDates]
        .sort((a, b) => a.date.localeCompare(b.date));
      const deduplicated = deduplicateHolidays(newHolidays);
      setHolidays(deduplicated);
      setIsDirty(true);
      setBulkInput('');
      setShowBulkImport(false);
      
      toast({
        title: "Holidays Imported",
        description: `${validDates.length} holiday${validDates.length > 1 ? 's' : ''} added to calendar.`,
      });
    }

    if (invalidDates.length > 0) {
      toast({
        title: "Invalid Dates",
        description: `${invalidDates.length} date${invalidDates.length > 1 ? 's were' : ' was'} invalid and skipped.`,
        variant: "destructive",
      });
    }
  };

  const performSave = useCallback(async (
    currentHolidays: Holiday[],
    currentExcludedApiDates: string[],
    currentCountryCode: string | null | undefined,
    currentRegionCode: string | null | undefined,
  ) => {
    setAutoSaveStatus('saving');
    try {
      const datesToSave = currentHolidays.map(h => h.date);
      const uniqueDates = Array.from(new Set(datesToSave)).sort();

      const additionalUpdates: { 
        country_code?: string | null; 
        region_code?: string | null;
        holiday_sync_status?: HolidaySyncStatus;
      } = {};

      const countryChanged = currentCountryCode !== initialCountryCode && 
                             !(currentCountryCode === '' && initialCountryCode === null) &&
                             !(currentCountryCode === null && initialCountryCode === '');
      const regionChanged = currentRegionCode !== initialRegionCode && 
                            !(currentRegionCode === '' && initialRegionCode === null) &&
                            !(currentRegionCode === null && initialRegionCode === '');

      if (countryChanged) {
        additionalUpdates.country_code = currentCountryCode || null;
      }
      if (regionChanged) {
        additionalUpdates.region_code = currentRegionCode || null;
      }

      additionalUpdates.holiday_sync_status = {
        ...holidaySyncStatus,
        excluded_api_dates: currentExcludedApiDates,
      };

      console.log('[HolidayCalendar] Auto-saving...', { 
        holidayCount: uniqueDates.length, 
        excludedCount: currentExcludedApiDates.length,
        additionalKeys: Object.keys(additionalUpdates) 
      });

      await onSave(uniqueDates, additionalUpdates);
      setIsDirty(false);
      setAutoSaveStatus('saved');
      
      // Clear "saved" indicator after 2s
      if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);
      savedIndicatorTimerRef.current = setTimeout(() => setAutoSaveStatus('idle'), 2000);
    } catch (error: any) {
      console.error('[HolidayCalendar] Auto-save failed:', error?.message || error);
      setIsDirty(false); // Break retry loop — user can click Retry manually
      setAutoSaveStatus('error');
      toast({
        title: "Auto-save failed",
        description: "Failed to save holiday calendar. Your changes are preserved locally.",
        variant: "destructive",
      });
    }
  }, [onSave, initialCountryCode, initialRegionCode, holidaySyncStatus]);

  // Keep a ref to performSave so the debounce effect doesn't re-fire on callback changes
  const performSaveRef = useRef(performSave);
  performSaveRef.current = performSave;

  // Debounced auto-save: triggers 1s after any user-initiated change
  useEffect(() => {
    // Skip initial mount — we only auto-save user-initiated changes
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (!isDirty) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      performSaveRef.current(holidays, excludedApiDates, countryCode, regionCode);
    }, 1000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [holidays, excludedApiDates, countryCode, regionCode, isDirty]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (savedIndicatorTimerRef.current) clearTimeout(savedIndicatorTimerRef.current);
    };
  }, []);

  const handleReset = () => {
    const holidayObjects: Holiday[] = (initialHolidays || []).map(date => ({ date }));
    setHolidays(holidayObjects);
    setExcludedApiDates(holidaySyncStatus?.excluded_api_dates ?? []);
    setCountryCode(initialCountryCode);
    setRegionCode(initialRegionCode);
    setIsDirty(false);
  };
  // Group holidays by year for better display
  const holidaysByYear = holidays.reduce((acc, holiday) => {
    const year = holiday.date.substring(0, 4);
    if (!acc[year]) acc[year] = [];
    acc[year].push(holiday);
    return acc;
  }, {} as Record<string, Holiday[]>);

  const currentYear = String(new Date().getFullYear());

  const toggleYear = (year: string) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Holiday Calendar</CardTitle>
        <CardDescription>
          Configure company holidays that affect allowance calculations and payroll
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Calendarific Sync Section */}
        <div className="space-y-4 p-4 border rounded-lg bg-accent/50">
          <div className="flex items-start gap-2">
            <Download className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1 space-y-3">
              <div>
                <h3 className="font-semibold text-sm">Sync Holidays from Calendarific</h3>
                <p className="text-sm text-muted-foreground">
                  Automatically import official holidays for your country/region
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select
                    value={countryCode || ''}
                    onValueChange={(value) => {
                      // Clear exclusions and region when country changes (they're country-specific)
                      if (value !== countryCode) {
                        setExcludedApiDates([]);
                        setRegionCode('');
                      }
                      setCountryCode(value);
                      setIsDirty(true);
                    }}
                  >
                    <SelectTrigger id="country">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">Region (Optional)</Label>
                  {/* Show dropdown when regions loaded, free-text fallback on error or no country */}
                  {isLoadingRegions ? (
                    <div className="flex items-center h-11 px-3 border rounded-md bg-background">
                      <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground mr-2" />
                      <span className="text-sm text-muted-foreground">Loading regions...</span>
                    </div>
                  ) : regions.length > 0 && !regionsError ? (
                    <Select
                      value={regionCode || ''}
                      onValueChange={(value) => {
                        setRegionCode(value === '__none__' ? '' : value);
                        setIsDirty(true);
                      }}
                    >
                      <SelectTrigger id="region">
                        <SelectValue placeholder="Select region (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None (national holidays only)</SelectItem>
                        {regions.map(region => (
                          <SelectItem key={region.iso} value={region.iso}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="region"
                      placeholder={regionsError ? "e.g., ca, ny, qc (auto-load failed)" : "e.g., ca, ny, qc"}
                      value={regionCode || ''}
                      onChange={(e) => {
                        setRegionCode(e.target.value);
                        setIsDirty(true);
                      }}
                    />
                  )}
                </div>
              </div>

              <Button
                onClick={handleSync}
                disabled={isSyncing || !countryCode}
                className="w-full"
                variant="default"
              >
                {isSyncing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync Holidays
                  </>
                )}
              </Button>

              {syncStatus && syncStatus.last_sync && (
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Last synced: {format(parseISO(syncStatus.last_sync), 'PPp')}</p>
                  <p>
                    Source: {syncStatus.source === 'api' ? 'Calendarific API' : 'Cached'}
                    {syncStatus.status === 'cache' && <span className="text-amber-600 dark:text-amber-500"> (cached)</span>}
                  </p>
                  <p>Holidays found: {holidays.length}</p>
                  {syncStatus.status === 'error' && syncStatus.error_message && (
                    <p className="text-destructive font-medium">
                      Error: {syncStatus.error_message}
                    </p>
                  )}
                </div>
              )}

              {/* Auto-sync toggle — only visible when a country is configured */}
              {countryCode && onAutoSyncChange && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="space-y-0.5">
                    <Label htmlFor="auto-sync" className="text-sm font-medium">Auto-sync yearly</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically import next year's holidays each January
                    </p>
                  </div>
                  <Switch
                    id="auto-sync"
                    checked={holidayAutoSync ?? false}
                    onCheckedChange={async (checked) => {
                      try {
                        await onAutoSyncChange(checked);
                        toast({
                          title: checked ? "Auto-sync enabled" : "Auto-sync disabled",
                          description: checked
                            ? "Holidays will be automatically synced each January."
                            : "You'll need to manually sync holidays each year.",
                        });
                      } catch {
                        toast({
                          title: "Error",
                          description: "Failed to update auto-sync setting.",
                          variant: "destructive",
                        });
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Add Holiday Section */}
        <div className="space-y-4">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label>Add Holiday</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal mt-2",
                      !selectedDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedDate ? format(selectedDate, 'PPP') : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Button 
              onClick={() => handleAddDate(selectedDate)}
              disabled={!selectedDate}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Date
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => setShowBulkImport(!showBulkImport)}
            className="w-full"
          >
            <Upload className="h-4 w-4 mr-2" />
            Bulk Import
          </Button>
        </div>

        {/* Bulk Import Section */}
        {showBulkImport && (
          <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
            <div className="space-y-2">
              <Label>Paste Dates (YYYY-MM-DD format, one per line)</Label>
              <Textarea
                placeholder="2025-01-01&#10;2025-07-04&#10;2025-12-25"
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                rows={6}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleBulkImport} disabled={!bulkInput.trim()}>
                Import Dates
              </Button>
              <Button variant="outline" onClick={() => {
                setBulkInput('');
                setShowBulkImport(false);
              }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Current Holidays Display */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">
              Current Holidays ({holidays.length})
            </Label>
          </div>

          {holidays.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 border border-dashed rounded-lg bg-muted/30">
              <CalendarIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                No holidays configured yet.<br />
                Add dates above to mark company holidays.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(holidaysByYear)
                .sort(([yearA], [yearB]) => yearB.localeCompare(yearA))
                .map(([year, dates]) => {
                  const isPastYear = year < currentYear;
                  const isExpanded = isPastYear ? !!expandedYears[year] : true;

                  return (
                    <Collapsible
                      key={year}
                      open={isExpanded}
                      onOpenChange={() => isPastYear && toggleYear(year)}
                    >
                      <div className="space-y-2">
                        {isPastYear ? (
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-2 w-full text-left group"
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <h4 className="font-medium text-sm text-muted-foreground">
                                {year}
                              </h4>
                              <Badge variant="secondary" className="text-xs">
                                {dates.length} holiday{dates.length !== 1 ? 's' : ''}
                              </Badge>
                              <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                                {isExpanded ? 'Hide' : 'Show'}
                              </span>
                            </button>
                          </CollapsibleTrigger>
                        ) : (
                          <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                            {year}
                            <Badge variant="secondary" className="text-xs">
                              {dates.length} holiday{dates.length !== 1 ? 's' : ''}
                            </Badge>
                          </h4>
                        )}
                        <CollapsibleContent>
                          <div className="grid gap-2">
                            {dates.map((holiday) => {
                              const date = parseISO(holiday.date);
                              return (
                                <div
                                  key={holiday.date}
                                  className="flex items-center justify-between p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className="font-medium">{format(date, 'EEEE, MMMM d')}</p>
                                         {holiday.source === 'api' && (
                                          <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                                            <Download className="h-3 w-3 mr-1" />
                                            Synced
                                          </Badge>
                                        )}
                                        {holiday.source === 'custom' && (
                                          <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                            Custom
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground">
                                        {holiday.name || holiday.date}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveDate(holiday.date)}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    title={holiday.source === 'api' ? 'Override synced holiday' : 'Remove custom holiday'}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  );
                })}
            </div>
          )}
        </div>

        {/* Excluded API Holidays (user can restore) */}
        {excludedApiDates.length > 0 && (
          <div className="space-y-3 p-4 border rounded-lg bg-muted/30 border-dashed">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium text-muted-foreground">
                Excluded from Sync ({excludedApiDates.length})
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              These API holidays have been removed and won't return on future syncs. Click restore to re-include them.
            </p>
            <div className="grid gap-1.5">
              {excludedApiDates.sort().map((date) => {
                const meta = holidaySyncStatus?.metadata?.find(m => m.date === date);
                const parsedDate = parseISO(date);
                return (
                  <div
                    key={date}
                    className="flex items-center justify-between p-2 rounded-md bg-background/50"
                  >
                    <div className="flex items-center gap-2">
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground line-through">
                        {isValid(parsedDate) ? format(parsedDate, 'MMM d, yyyy') : date}
                        {meta?.name ? ` — ${meta.name}` : ''}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRestoreExcludedHoliday(date)}
                      className="h-7 text-xs text-primary hover:text-primary"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Restore
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Impact Notice */}
        {holidays.length > 0 && (
          <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Holiday Allowance Impact
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  These dates will be flagged as holidays in analytics. Employees working on these dates 
                  may be eligible for holiday allowances based on configured compensation rules.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Auto-save Status Indicator */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t text-sm text-muted-foreground">
          {autoSaveStatus === 'saving' && (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Saving...</span>
            </>
          )}
          {autoSaveStatus === 'saved' && (
            <>
              <Check className="h-4 w-4 text-primary" />
              <span>All changes saved</span>
            </>
          )}
          {autoSaveStatus === 'error' && (
            <>
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-destructive">Save failed</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => performSave(holidays, excludedApiDates, countryCode, regionCode)}
              >
                Retry
              </Button>
            </>
          )}
          {autoSaveStatus === 'idle' && holidays.length > 0 && (
            <span className="text-xs">Changes auto-save</span>
          )}
        </div>
      </CardContent>

      {/* Delete API Holiday Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Exclude Synced Holiday?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  You are about to remove <strong>{holidayToDelete?.name}</strong> ({holidayToDelete?.date && format(parseISO(holidayToDelete.date), 'MMMM d, yyyy')}) 
                  which was synced from Calendarific.
                </p>
                <p className="text-amber-600 dark:text-amber-500 font-medium">
                  ⚠️ This holiday will be excluded from all future syncs. You can restore it later from the "Excluded from Sync" section.
                </p>
                <p>
                  Are you sure you want to exclude this holiday?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setHolidayToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteApiHoliday} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Yes, Override Holiday
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
